import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail,
  Phone,
  MapPin,
  Building2,
  Globe,
  Loader2,
  Shield,
  CheckCircle,
  XCircle,
  FileText,
  Tag,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { RemoteDataSource, Settings } from "@/datasources/remote_datasource";
import { cn } from "@/lib/utils";
import { resolveMediaUrl } from "@/lib/media";
import { NotFoundState } from "@/components/ui/empty-state";
import { Logo } from "@/types/media";
import { AppConfig } from "@/datasources/AppConfig";

const api = new RemoteDataSource(Settings.urlBase);

interface Endereco {
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  pais?: string;
  ibge?: string;
}

interface Supervisora {
  id: string;
  nome: string;
  cnpj: string;
  arquivada: boolean;
  geral: boolean;
  logo?: Logo | null;
  razaoSocial?: string | null;
  naturezaJuridica?: string | null;
  nomeFantasia?: string | null;
  email?: string | null;
  telefone?: string | null;
  integracao?: string | null;
  etiquetas?: string[] | null;
  latitude?: number;
  longitude?: number;
  altitude?: number;
  endereco?: Endereco | null;
}

function unwrap(raw: unknown): Supervisora | null {
  if (!raw) return null;
  if (Array.isArray(raw)) return (raw[0] as Supervisora) ?? null;
  const r = raw as Record<string, unknown>;
  if (Array.isArray(r.data) && (r.data as unknown[])[0]) return (r.data as Supervisora[])[0];
  if (r.data && typeof r.data === "object") return r.data as Supervisora;
  if (Array.isArray(r.item) && (r.item as unknown[])[0]) return (r.item as Supervisora[])[0];
  if (r.id) return r as unknown as Supervisora;
  return null;
}

export default function SupervisoraPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [supervisora, setSupervisora] = useState<Supervisora | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSupervisora = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.get<Supervisora>({
        endpoint: `supervisora`,
        headers: { Accept: "application/vnd.data.full.v1+json" },
        queryParams: AppConfig.municipioId ? { municipioId: AppConfig.municipioId } : undefined,
        forceRefresh: true,
        serialize: (raw) => (unwrap(raw) ?? ({} as Supervisora)),
      });

      if ("failure" in result) {
        throw new Error(result.failure.messages?.join(", ") || "Falha ao carregar supervisora");
      }

      const data = result.success.data;
      if (!data || !data.id) {
        setSupervisora(null);
        return;
      }
      setSupervisora(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erro desconhecido";
      setError(msg);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível carregar os dados da ouvidoria supervisora.",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchSupervisora();
  }, [fetchSupervisora]);

  useEffect(() => {
    const handleFocus = () => fetchSupervisora();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") fetchSupervisora();
    };
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [fetchSupervisora]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Carregando supervisora...</span>
      </div>
    );
  }

  if (error || !supervisora) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <NotFoundState
          resourceName="ouvidoria supervisora"
          onGoBack={() => navigate("/")}
        />
      </div>
    );
  }

  const avatarFallback = supervisora.nome ? supervisora.nome.slice(0, 2).toUpperCase() : "OU";
  const enderecoCompleto = supervisora.endereco
    ? [
        supervisora.endereco.logradouro,
        supervisora.endereco.numero,
        supervisora.endereco.complemento,
      ]
        .filter(Boolean)
        .join(", ")
    : "";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="heading-page">Ouvidoria Supervisora</h1>
        <p className="text-muted-foreground mt-1">
          Detalhes da ouvidoria responsável por supervisionar esta ouvidoria setorial
        </p>
      </div>

      {/* Main Content */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center text-center">
                <Avatar className="h-24 w-24 mb-4">
                  {supervisora.logo?.arquivo ? (
                    <AvatarImage src={resolveMediaUrl(supervisora.logo.arquivo) || supervisora.logo.arquivo} alt={supervisora.nome} />
                  ) : null}
                  <AvatarFallback className="text-2xl bg-gradient-to-br from-primary/20 to-secondary/20">
                    {avatarFallback}
                  </AvatarFallback>
                </Avatar>

                <h2 className="text-xl font-semibold">
                  {supervisora.nome || "Nome não informado"}
                </h2>
                {supervisora.nomeFantasia && (
                  <p className="text-sm text-muted-foreground">
                    {supervisora.nomeFantasia}
                  </p>
                )}

                <div className="flex items-center gap-2 mt-2 flex-wrap justify-center">
                  <Badge
                    variant={supervisora.arquivada ? "destructive" : "default"}
                    className={cn(
                      !supervisora.arquivada &&
                        "bg-success/10 text-success border-success/20"
                    )}
                  >
                    {supervisora.arquivada ? (
                      <>
                        <XCircle className="h-3 w-3 mr-1" />
                        Arquivada
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Ativa
                      </>
                    )}
                  </Badge>
                  {supervisora.geral && (
                    <Badge variant="secondary">
                      <Shield className="h-3 w-3 mr-1" />
                      Geral
                    </Badge>
                  )}
                </div>

                <Separator className="my-4" />

                <div className="w-full space-y-3 text-left">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <FileText className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">CNPJ</p>
                      <p className="text-sm font-medium">
                        {supervisora.cnpj || "Não informado"}
                      </p>
                    </div>
                  </div>

                  {supervisora.razaoSocial && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Building2 className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Razão Social</p>
                        <p className="text-sm font-medium">{supervisora.razaoSocial}</p>
                      </div>
                    </div>
                  )}

                  {supervisora.naturezaJuridica && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Building2 className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Natureza Jurídica</p>
                        <p className="text-sm font-medium">
                          {supervisora.naturezaJuridica}
                        </p>
                      </div>
                    </div>
                  )}

                  {supervisora.email && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Mail className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">E-mail</p>
                        <p className="text-sm font-medium break-all">
                          {supervisora.email}
                        </p>
                      </div>
                    </div>
                  )}

                  {supervisora.telefone && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Phone className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Telefone</p>
                        <p className="text-sm font-medium">{supervisora.telefone}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Details Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="lg:col-span-2"
        >
          <Card>
            <CardHeader>
              <CardTitle>Informações da Supervisora</CardTitle>
              <CardDescription>
                Detalhes completos da ouvidoria que supervisiona esta setorial
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {supervisora.endereco && (
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <MapPin className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Endereço</p>
                      {enderecoCompleto && (
                        <p className="text-sm font-medium">{enderecoCompleto}</p>
                      )}
                      {(supervisora.endereco.bairro ||
                        supervisora.endereco.cidade ||
                        supervisora.endereco.estado) && (
                        <p className="text-sm text-muted-foreground">
                          {[
                            supervisora.endereco.bairro,
                            supervisora.endereco.cidade &&
                            supervisora.endereco.estado
                              ? `${supervisora.endereco.cidade}/${supervisora.endereco.estado}`
                              : supervisora.endereco.cidade ||
                                supervisora.endereco.estado,
                          ]
                            .filter(Boolean)
                            .join(" - ")}
                        </p>
                      )}
                      {supervisora.endereco.cep && (
                        <p className="text-sm text-muted-foreground">
                          CEP: {supervisora.endereco.cep}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {(supervisora.latitude || supervisora.longitude) && (
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Globe className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Coordenadas</p>
                      <p className="text-sm font-medium">
                        Latitude: {supervisora.latitude?.toFixed(6) ?? "-"} | Longitude:{" "}
                        {supervisora.longitude?.toFixed(6) ?? "-"}
                      </p>
                      {typeof supervisora.altitude === "number" && (
                        <p className="text-sm text-muted-foreground">
                          Altitude: {supervisora.altitude}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {supervisora.etiquetas && supervisora.etiquetas.length > 0 && (
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Tag className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground mb-2">Etiquetas</p>
                      <div className="flex flex-wrap gap-2">
                        {supervisora.etiquetas.map((etiqueta, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {etiqueta}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {!supervisora.endereco &&
                  !supervisora.latitude &&
                  !supervisora.longitude &&
                  (!supervisora.etiquetas || supervisora.etiquetas.length === 0) && (
                    <p className="text-sm text-muted-foreground">
                      Sem informações adicionais cadastradas.
                    </p>
                  )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <div className="flex justify-end">
        <Button variant="outline" onClick={fetchSupervisora}>
          Atualizar
        </Button>
      </div>
    </div>
  );
}
